export interface IUploadPreflightRule {
	allowedExtensions: string[];
	allowedMimeTypesByExtension: Record<string, string[]>;
	maxBytes: number;
}

export interface IUploadPreflightResult {
	messageKey?: string;
	messageValues?: Record<string, string>;
	ok: boolean;
}

export const MB = 1024 * 1024;

export const JSON_UPLOAD_RULE: IUploadPreflightRule = {
	allowedExtensions: [".json"],
	allowedMimeTypesByExtension: {
		".json": ["application/json", "text/json"],
	},
	maxBytes: 20 * MB,
};

export const DT_STIMULI_UPLOAD_RULE: IUploadPreflightRule = {
	allowedExtensions: [".csv", ".xlsx", ".pptx"],
	allowedMimeTypesByExtension: {
		".csv": ["text/csv", "application/csv", "application/vnd.ms-excel"],
		".xlsx": ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
		".pptx": ["application/vnd.openxmlformats-officedocument.presentationml.presentation"],
	},
	maxBytes: 10 * MB,
};

export const DT_PRODUCT_CONTEXT_UPLOAD_RULE: IUploadPreflightRule = {
	allowedExtensions: [".pptx", ".txt"],
	allowedMimeTypesByExtension: {
		".pptx": [
			"application/vnd.openxmlformats-officedocument.presentationml.presentation",
			"application/octet-stream",
		],
		".txt": ["text/plain", "application/octet-stream"],
	},
	maxBytes: 20 * MB,
};

export const PERSONA_CHAT_UPLOAD_RULE: IUploadPreflightRule = {
	allowedExtensions: [".csv", ".xlsx", ".pptx", ".png", ".jpg", ".jpeg", ".webp"],
	allowedMimeTypesByExtension: {
		".csv": ["text/csv", "application/csv", "application/vnd.ms-excel"],
		".xlsx": ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
		".pptx": ["application/vnd.openxmlformats-officedocument.presentationml.presentation"],
		".png": ["image/png"],
		".jpg": ["image/jpeg"],
		".jpeg": ["image/jpeg"],
		".webp": ["image/webp"],
	},
	maxBytes: 5 * MB,
};

export const PERSONA_CHAT_UPLOAD_ACCEPT = PERSONA_CHAT_UPLOAD_RULE.allowedExtensions.join(",");

export const PERSONA_SEGMENTATION_UPLOAD_RULE: IUploadPreflightRule = {
	allowedExtensions: [".pptx"],
	allowedMimeTypesByExtension: {
		".pptx": [
			"application/vnd.openxmlformats-officedocument.presentationml.presentation",
			"application/octet-stream",
		],
	},
	maxBytes: 50 * MB,
};

export const formatBytesAsMb = (bytes: number): string => `${Math.floor(bytes / MB)} MB`;

export const getFileExtension = (filename: string): string => {
	const normalized = filename.toLowerCase();
	const dotIndex = normalized.lastIndexOf(".");
	return dotIndex >= 0 ? normalized.slice(dotIndex) : "";
};

export const validateUploadPreflight = (
	file: File,
	rule: IUploadPreflightRule
): IUploadPreflightResult => {
	const extension = getFileExtension(file.name);
	if (!rule.allowedExtensions.includes(extension)) {
		return {
			ok: false,
			messageKey: "Unsupported file type. Allowed: {{types}}",
			messageValues: { types: rule.allowedExtensions.join(", ") },
		};
	}

	if (file.size > rule.maxBytes) {
		return {
			ok: false,
			messageKey: "File exceeds the {{limit}} limit.",
			messageValues: { limit: formatBytesAsMb(rule.maxBytes) },
		};
	}

	const normalizedMime = (file.type || "").split(";", 1)[0].trim().toLowerCase();
	const allowedMimeTypes = rule.allowedMimeTypesByExtension[extension] || [];
	if (!normalizedMime) {
		return {
			ok: false,
			messageKey: "Missing content type for {{extension}}.",
			messageValues: { extension },
		};
	}
	if (normalizedMime && !allowedMimeTypes.includes(normalizedMime)) {
		return {
			ok: false,
			messageKey: "Unsupported content type for {{extension}}.",
			messageValues: { extension },
		};
	}

	return { ok: true };
};
